import type { ReactNode } from 'react'
import {
  Award,
  Calendar,
  Check,
  ChevronRight,
  ClipboardList,
  CreditCard,
  MessageCircle,
  Trophy,
  User,
  Users,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@shared/lib'
import { Badge, BadgeVariant, Button, ButtonSize, ButtonVariant } from '@shared/ui'
import { NotificationStatus } from '../model/types'
import { NotifIconType } from './constants'

// ── Icon config ───────────────────────────────────────────────────────────────

const ICON_CONFIG: Record<NotifIconType, { icon: ReactNode; bg: string; fg: string }> = {
  task: {
    icon: <ClipboardList className="size-5" />,
    bg: 'bg-violet-subtle',
    fg: 'text-violet',
  },
  olympiad: {
    icon: <Calendar className="size-5" />,
    bg: 'bg-violet-subtle',
    fg: 'text-violet',
  },
  user: {
    icon: <User className="size-5" />,
    bg: 'bg-blue-subtle',
    fg: 'text-blue',
  },
  users: {
    icon: <Users className="size-5" />,
    bg: 'bg-blue-subtle',
    fg: 'text-blue',
  },
  message: {
    icon: <MessageCircle className="size-5" />,
    bg: 'bg-orange-subtle',
    fg: 'text-orange',
  },
  card: {
    icon: <CreditCard className="size-5" />,
    bg: 'bg-blue-subtle',
    fg: 'text-blue',
  },
  certificate: {
    icon: <Award className="size-5" />,
    bg: 'bg-violet-subtle',
    fg: 'text-violet',
  },
  result: {
    icon: <Trophy className="size-5" />,
    bg: 'bg-violet-subtle',
    fg: 'text-violet',
  },
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface NotificationCardProps {
  iconType: NotifIconType
  title: string
  timeLabel: string
  category: string
  badge?: ReactNode
  body?: ReactNode
  navigable?: boolean
  onNavigate?: () => void
  onApprove?: () => void
  onDecline?: () => void
  /** When set, the card shows an approved/declined status badge in place of the
   *  action buttons. */
  status?: NotificationStatus
  /** Запрос по заявке в пути: обе кнопки недоступны. */
  busy?: boolean
  /** Пояснение, почему заявку нельзя одобрить/отклонить; показывается вместо молчаливого тоста. */
  notice?: string
  /** Пояснение — не сбой (заявка уже обработана): спокойный цвет вместо красного. */
  noticeNeutral?: boolean
  approveDisabled?: boolean
  /** Заявку уже обработали — кнопок нет. */
  actionsHidden?: boolean
  /** Новое уведомление среди прочитанных: точка у заголовка (для скринридера — подпись «Новое»). */
  unread?: boolean
  className?: string
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NotificationCard({
  iconType,
  title,
  timeLabel,
  category,
  badge,
  body,
  navigable,
  onNavigate,
  onApprove,
  onDecline,
  status,
  busy,
  notice,
  noticeNeutral,
  approveDisabled,
  actionsHidden,
  unread,
  className,
}: NotificationCardProps) {
  const { t } = useTranslation('admin')
  const { icon, bg, fg } = ICON_CONFIG[iconType]
  const hasActions = !status && !actionsHidden && onApprove && onDecline
  const isClickable = !!onNavigate

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onNavigate}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onNavigate?.()
              }
            }
          : undefined
      }
      className={cn(
        'bg-surface border-border flex gap-3 rounded-xl border p-4',
        isClickable && 'hover:bg-orange-subtle cursor-pointer transition-colors',
        className
      )}
    >
      {/* Icon */}
      <span
        className={cn('flex size-8 shrink-0 items-center justify-center rounded-full p-2', bg, fg)}
      >
        {icon}
      </span>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <p className="text-foreground truncate text-sm font-bold">{title}</p>
            {badge}
            {unread && (
              <span className="bg-orange size-2 shrink-0 rounded-full">
                <span className="sr-only">{t('notification.new_mark')}</span>
              </span>
            )}
          </div>
          {navigable && <ChevronRight className="text-secondary mt-0.5 size-3 shrink-0" />}
        </div>

        {/* Body */}
        {body && (
          <div className="border-border/40 bg-border/20 w-full rounded-2xl px-3 py-2 text-sm">
            {body}
          </div>
        )}

        {/* Actions / resolved status */}
        {status ? (
          <div>
            {status === NotificationStatus.approved ? (
              <Badge variant={BadgeVariant.green} iconLeft={<Check className="size-3" />}>
                {t('notification.approved')}
              </Badge>
            ) : (
              <Badge variant={BadgeVariant.red} iconLeft={<X className="size-3" />}>
                {t('notification.declined')}
              </Badge>
            )}
          </div>
        ) : (
          hasActions && (
            <div className="flex gap-3">
              <Button
                variant={ButtonVariant.outline}
                size={ButtonSize.sm}
                onClick={onDecline}
                disabled={busy}
              >
                {t('notification.decline')}
              </Button>
              <Button
                variant={ButtonVariant.primary}
                size={ButtonSize.sm}
                onClick={onApprove}
                disabled={busy || approveDisabled}
              >
                {t('notification.approve')}
              </Button>
            </div>
          )
        )}

        {notice && !status && (
          <p
            role="status"
            className={cn('typo-base', noticeNeutral ? 'text-secondary' : 'text-red-primary')}
          >
            {notice}
          </p>
        )}

        {/* Footer */}
        <div className="text-secondary flex items-center gap-2 text-xs">
          <span>{timeLabel}</span>
          <span className="bg-secondary h-3 w-px" />
          <span>{category}</span>
        </div>
      </div>
    </div>
  )
}
